import { useEffect, useMemo, useState } from 'react';

import { useError } from '../../../error-provider/ErrorProvider';
import { Box, Button, Divider, Select, Stack, Text, useDisclosure, useToast, Flex, Switch } from '@chakra-ui/react';
import { useApolloClient, useMutation } from '@apollo/client';
import { CallTransfer, DeleteCallTransfer } from '../../../graphql/mutations/CallTransfer';
import ZaraButton from '../../custom-components/buttons/Button';
import PhoneInput from 'react-phone-input-2';
import Input from '../../custom-components/Inputs/Input';
import DeleteTransferrCall from '../../modals/DeleteTransferrCall';
import CloseButton from '../../custom-components/buttons/CloseButton';
import { countries } from '../../../utils/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const callerIdOptions = [
    { value: 1, text: "Show caller's phone numbers" },
    { value: 2, text: "Show AI agent's phone number" },
];

const CallTransferSwitch = ({ data, agentId }) => {
    const toast = useToast();
    const { showError } = useError();

    const [onCallTransfer, { loading }] = useMutation(CallTransfer, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const [onDeleteTransferCall, { loading: isDeleteTransferCallLoading }] = useMutation(DeleteCallTransfer, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const { isOpen, onOpen, onClose } = useDisclosure();

    const apolloClient = useApolloClient();

    const [selectedCallId, setSelectedCallId] = useState('');

    const [fields, setFields] = useState([
        {
            __typename: '',
            id: '',
            phoneNumber: '',
            instructionBeforTransfer: '',
            beforTransferingMsg: '',
            callerIdStatus: 1,
        },
    ]);

    const inputsVariables = () => {
        return fields.map((item) => {
            const newObj = Object.entries(item)
                .filter((item) => !item.includes('__typename'))
                .reduce((accum, [key, value]) => {
                    accum[key] = value;

                    return accum;
                }, {});

            return newObj;
        });
    };

    const { active, callTransfers } = useMemo(() => {
        const active = data?.active ?? false;
        const callTransfers = data?.callTransfers ?? [];

        return { active, callTransfers };
    }, [data]);

    useEffect(() => {
        if (callTransfers.length) {
            const transfers = [...callTransfers]
                .map((item) => {
                    const newObj = Object.entries(item).reduce((accum, [key, value]) => {
                        if ((key === 'phoneNumber' && value === '') || (key === 'instructionBeforTransfer' && !value)) {
                            accum[key] = '';
                        } else {
                            accum[key] = value;
                        }
                        return accum;
                    }, {});

                    return newObj;
                })
                .reverse();

            setFields(transfers);
            return;
        }
    }, [callTransfers]);

    const handleInputChange = (index, property, value) => {
        setFields((prevFields) => {
            const newFields = [...prevFields];
            newFields[index] = {
                ...newFields[index],
                [property]: value,
            };
            return newFields;
        });
    };

    async function onSubmit(e) {
        e.preventDefault();

        const variables = {
            callTransferActive: data?.active,
            callTransfers: inputsVariables(),
            agentId: agentId,
        };
        try {
            await onCallTransfer({
                variables: variables,
                onCompleted(data) {
                    if (data.callTransfer.updateOrCreate.__typename === 'CallTransferSuccessType') {
                        toast({
                            status: 'success',
                            title: data.callTransfer.updateOrCreate.message,
                            isClosable: true,
                        });

                        apolloClient.cache.modify({
                            id: `AgentType:${agentId}`,
                            fields: {
                                callTransferActive() {
                                    return active;
                                },
                                callTransfer() {
                                    return data.callTransfer.updateOrCreate.callTransfers;
                                },
                            },
                        });
                    }
                },
            });
        } catch (error) { }
    }

    async function onDeleteNumber() {
        if (!selectedCallId) {
            setFields((prev) => prev.slice(0, 1));

            onClose();
            return;
        }
        try {
            await onDeleteTransferCall({
                variables: {
                    callTransferId: selectedCallId,
                },
                onCompleted(data) {
                    if (data.callTransfer.delete.__typename === 'SuccessType') {
                        setFields((prev) => prev.filter((field) => field.id !== selectedCallId));
                        toast({
                            status: 'success',
                            title: data.callTransfer.delete.message,
                            isClosable: true,
                        });
                        onClose();

                        apolloClient.cache.modify({
                            id: `AgentType:${agentId}`,
                            fields: {
                                callTransferActive() {
                                    return active;
                                },
                                callTransfer() {
                                    return callTransfers?.filter((call) => call?.id !== selectedCallId);
                                },
                            },
                        });
                    } else {
                        toast({
                            status: 'error',
                            title: data.callTransfer.delete.message,
                            isClosable: true,
                        });
                    }
                },
            });
        } catch (error) { }
    }

    async function changeTransfer(isActive) {
        const variables = {
            callTransferActive: isActive,
            callTransfers: inputsVariables(),
            agentId: agentId,
        };
        await onCallTransfer({
            variables: variables,
            onCompleted(data) {
                if (data.callTransfer.updateOrCreate.__typename === 'CallTransferSuccessType') {
                    apolloClient.cache.modify({
                        id: `AgentType:${agentId}`,
                        fields: {
                            callTransferActive() {
                                return isActive;
                            },
                            callTransfer() {
                                return data.callTransfer.updateOrCreate.callTransfers;
                            },
                        },
                    });
                }
            },
        });
    }

    const inputName = (index) => {
        switch (index) {
            case 2:
                return 'Phone Number';
            case 3:
                return 'When do you want your agent to transfer calls to this number?';
            case 4:
                return 'What do you want your agent to say before it transfers the call?';
            case 5:
                return 'Caller ID';
            default:
                return '';
        }
    };

    const placeHolder = (index) => {
        switch (index) {
            case 2:
                return 'Enter phone number';
            case 3:
                return 'e.g., If the caller has an emergency';
            case 4:
                return `e.g., I'll transfer you to our emergency line`;
            case 5:
                return '';
            default:
                return '';
        }
    };

    const addAnotherNumer = () => {
        setFields((prev) => [
            ...prev,
            {
                __typename: '',
                id: '',
                phoneNumber: '',
                instructionBeforTransfer: '',
                beforTransferingMsg: '',
                callerIdStatus: 1,
            },
        ]);
    };

    return (
        <Box as='form' onSubmit={onSubmit}>
            {/* 
                NOTE: This block is commented out because it’s currently not part of the design, 
                but it's kept here in case we need to reintroduce it later. 
            */}
            <Flex
                fontSize={{ base: '24px', sm: '28px' }}
                lineHeight='28px'
                fontWeight={{ base: '400', sm: '700' }}
                textAlign='left'
                gap='10px'
                alignItems='center'
                mb='10px'
            >
                <Switch
                    isDisabled={loading}
                    isChecked={data.active}
                    onChange={(v) => changeTransfer(data?.active ? false : true)}
                    size='lg'
                />
                <Text>Transfer Calls</Text>
            </Flex>

            <Stack spacing='5'>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                    {fields.map((field, index) => (
                        <Stack
                            key={index}
                            pos='relative'
                            p={{ base: '0px', sm: '15px' }}
                            borderRadius='6px'
                            border='1px solid'
                            borderColor='gray.200'
                            css={{
                                '@media only screen and (max-width: 450px)': {
                                    border: 'none',
                                },
                            }}
                            spacing='20px'
                        >
                            {index && (
                                <CloseButton
                                    css={{
                                        '@media only screen and (max-width: 450px)': {
                                            display: 'none',
                                        },
                                    }}
                                    onClick={() => {
                                        setSelectedCallId(field.id);
                                        onOpen();
                                    }}
                                />
                            )}
                            {Object.keys(field).map(
                                (property, keyIndex) =>
                                    property !== 'id' &&
                                    property !== '__typename' && (
                                        <Box key={property}>
                                            <Text fontWeight='bold'>{inputName(keyIndex)}</Text>
                                            {property === 'phoneNumber' ? (
                                                <PhoneInput
                                                    inputStyle={{ width: '100%' }}
                                                    enableSearch={true}
                                                    onlyCountries={countries}
                                                    country={'us'}
                                                    inputProps={{ required: true }}
                                                    value={field[property]}
                                                    onChange={(e) => handleInputChange(index, property, e)}
                                                />
                                            ) : property !== 'callerIdStatus' ? (
                                                <Input
                                                    w='full'
                                                    placeholder={placeHolder(keyIndex)}
                                                    value={field[property]}
                                                    onChange={(e) => handleInputChange(index, property, e.target.value)}
                                                />
                                            ) : (
                                                <Stack spacing='5' w='full'>
                                                    <Stack spacing='5px'>
                                                        <Select
                                                            onChange={(e) =>
                                                                handleInputChange(
                                                                    index,
                                                                    property,
                                                                    Number(e.target.value)
                                                                )
                                                            }
                                                            value={field[property]}
                                                        >
                                                            {callerIdOptions.map((item) => (
                                                                <option key={item.value} value={item.value}>
                                                                    {item.text}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    </Stack>
                                                </Stack>
                                            )}
                                        </Box>
                                    )
                            )}

                            {index && (
                                <Button
                                    display='none'
                                    css={{
                                        '@media only screen and (max-width: 450px)': {
                                            display: 'block',
                                        },
                                    }}
                                    colorScheme='red'
                                    onClick={() => {
                                        setSelectedCallId(field.id);
                                        onOpen();
                                    }}
                                >
                                    Delete Number
                                </Button>
                            )}
                            {fields.length > 1 && index !== fields.length - 1 && (
                                <Divider
                                    display='none'
                                    css={{
                                        '@media only screen and (max-width: 450px)': {
                                            display: 'block',
                                        },
                                    }}
                                />
                            )}
                        </Stack>
                    ))}
                </div>
                <Stack direction='row'>
                    <ZaraButton
                        onClick={addAnotherNumer}
                        w='auto'
                        fontSize='sm'
                        leftIcon={<FontAwesomeIcon icon={faPlus} />}
                    >
                        Add another call transfer
                    </ZaraButton>{' '}
                    <ZaraButton type='submit' isLoading={loading} w='auto' fontSize='sm'>
                        Save
                    </ZaraButton>
                </Stack>
            </Stack>

            <DeleteTransferrCall
                onDeleteNumber={onDeleteNumber}
                isOpen={isOpen}
                onClose={onClose}
                isLoading={isDeleteTransferCallLoading}
            />
        </Box>
    );
};

export default CallTransferSwitch;
